import { useTranslation } from "react-i18next";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { TrashSimpleIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useRef, useState, useEffect, useId } from "react";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { toast } from "sonner";
import { useUpdateUserProfile } from "../services/useUpdateUserProfile";
import { useDeleteUserPhoto } from "../services/useDeleteUserPhoto";

export function UserSettings() {
  const { t } = useTranslation(["settings", "common"]);
  const { firstName, lastName, profileImageUrl, refetch } = useMeStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const firstNameId = useId();
  const lastNameId = useId();

  const { mutate: updateUserProfile, isPending: isSaving } = useUpdateUserProfile();
  const { mutate: deleteUserPhoto, isPending: isDeleting } = useDeleteUserPhoto();

  const [formData, setFormData] = useState({ firstName: "", lastName: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({ firstName: firstName ?? "", lastName: lastName ?? "" });
  }, [firstName, lastName]);

  const isChanged = formData.firstName !== firstName || formData.lastName !== lastName || selectedFile !== null;

  function fileClickHandler() {
    fileInputRef.current?.click();
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error(t("profile.invalidFileType"));
      return t("profile.invalidFileType");
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("profile.fileTooLarge"));
      return t("profile.fileTooLarge");
    }

    return null;
  }

  async function fileChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleRemovePhoto = async () => {
    setUploadError(null);

    deleteUserPhoto(undefined, {
      onSuccess: async () => {
        toast.success(t("profile.photoRemoved"));
        if (refetch) await refetch();
      },
      onError: (error) => {
        console.error("Failed to remove profile photo:", error);
        const err = error as { response?: { data?: { message?: string } } };
        setUploadError(err.response?.data?.message || t("profile.photoRemoveFailed"));
        toast.error(t("profile.photoRemoveFailed"));
      }
    });
  };

  const handleSaveChanges = async () => {
    setUploadError(null);

    updateUserProfile(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        file: selectedFile ?? undefined
      },
      {
        onSuccess: async () => {
          toast.success(t("profile.updated"));
          if (refetch) await refetch();

          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: (error) => {
          toast.error(t("profile.updateFailed"));
          console.error("Failed to update profile:", error);
        }
      }
    );
  };

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <p className="typo-h6 text-innoveraZinc-900">{t("profile.heading")}</p>

      <div className="flex flex-col gap-8">
        <div className="flex gap-6">
          <button
            data-testid="user-settings-avatar-button"
            className={cn(
              "border-none outline-hidden cursor-pointer flex items-center justify-center",
              "w-[88px] h-[88px] rounded-full bg-innoveraOrange-400 overflow-hidden"
            )}
            onClick={fileClickHandler}
          >
            {(previewUrl || profileImageUrl) && (
              <img src={previewUrl || profileImageUrl || ""} alt="user img" className="w-full h-full object-cover" />
            )}
            {!previewUrl && !profileImageUrl && <p className="typo-body-md text-[40px] text-white">{firstName?.[0]}</p>}
          </button>
          <input
            data-testid="user-settings-photo-input"
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={fileChangeHandler}
          />

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                data-testid="user-settings-upload-photo-button"
                onClick={fileClickHandler}
                disabled={isSaving || isDeleting}
              >
                <UploadSimpleIcon />
                {t("profile.uploadPhoto")}
              </Button>
              {profileImageUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="user-settings-remove-photo-button"
                  onClick={handleRemovePhoto}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                      {t("profile.removing")}
                    </>
                  ) : (
                    <>
                      <TrashSimpleIcon />
                      {t("profile.removePhoto")}
                    </>
                  )}
                </Button>
              )}
            </div>
            <div>
              <p className="typo-caption text-innoveraZinc-500">{t("profile.photoHintSize")}</p>
              <p className="typo-caption text-innoveraZinc-500">{t("profile.photoHintFormat")}</p>
              {uploadError && <p className="typo-caption text-red-500 mt-1">{uploadError}</p>}
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex gap-1">
          <Field className="gap-2">
            <FieldLabel htmlFor={firstNameId}>{t("profile.firstName")}</FieldLabel>
            <Input
              id={firstNameId}
              data-testid="user-settings-first-name-input"
              className="w-[250px]"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              disabled={isSaving || isDeleting}
            />
          </Field>
          <Field className="gap-2">
            <FieldLabel htmlFor={lastNameId}>{t("profile.lastName")}</FieldLabel>
            <Input
              id={lastNameId}
              data-testid="user-settings-last-name-input"
              className="w-[250px]"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              disabled={isSaving || isDeleting}
            />
          </Field>
        </div>

        <Button
          type="button"
          data-testid="user-settings-save-button"
          className="w-fit"
          onClick={handleSaveChanges}
          disabled={isSaving || isDeleting || !isChanged}
        >
          {isSaving ? (
            <>
              <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
              {t("common.saving")}
            </>
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
